import type { JSX, ReactNode } from 'react'

import { clsx } from 'clsx'

import { Text } from '@buffer-mono/popcorn'

import { ExpandedChannelSection } from '~publish/components/Composer/components/ExpandedChannelSection'
import * as UploadDropzone from '~publish/components/UploadDropzone'

import type { Service } from '../../graphqlEnums'

import styles from './ExpandedComposerLayout.module.css'

export type ExpandedComposerLayoutThreadClassNames = Readonly<{
  editor: string
  editorContainer: string
}>

export type ExpandedComposerLayoutProps = Readonly<{
  service?: Service
  isOmni?: boolean
  error?: string
  postTypeBars?: ReactNode
  notices?: ReactNode
  uploadDisabled?: boolean
  onUploadDrop?: (files: File[]) => void
  onUploadPaste?: (files: File[]) => void
  editor?: ReactNode
  attachments?: ReactNode
  toolbar?: ReactNode
  renderThread?: (
    classNames: ExpandedComposerLayoutThreadClassNames,
  ) => ReactNode
  channelFields?: ReactNode
}>

export function ExpandedComposerLayout({
  service,
  isOmni = false,
  error,
  postTypeBars,
  notices,
  uploadDisabled,
  onUploadDrop,
  onUploadPaste,
  editor,
  attachments,
  toolbar,
  renderThread,
  channelFields,
}: ExpandedComposerLayoutProps): JSX.Element {
  return (
    <ExpandedChannelSection
      error={error}
      className={clsx(styles.channelSection, isOmni && styles.omnibox)}
    >
      {service !== undefined && (
        <ExpandedChannelSection.ChannelIcon
          service={service}
          className={styles.channelIcon}
        />
      )}
      <ExpandedChannelSection.ChannelContent className={styles.channelContent}>
        {postTypeBars}
        {renderThread === undefined && (
          <>
            {notices}
            <div className={styles.editorMediaContainer}>
              <div className={styles.editorContainer}>
                <UploadDropzone.Root
                  disabled={uploadDisabled}
                  className={styles.composerUploadDropzone}
                  onDrop={onUploadDrop}
                  onPaste={onUploadPaste}
                >
                  {editor}
                  <UploadDropzone.Overlay
                    className={styles.composerUploadDropzoneOverlay}
                  >
                    <Text size="lg" color="subtle" weight="bold">
                      Drop files to upload
                    </Text>
                  </UploadDropzone.Overlay>
                </UploadDropzone.Root>
              </div>
              {attachments}
              {toolbar}
            </div>
          </>
        )}
        {renderThread?.({
          editor: styles.editorContainer,
          editorContainer: styles.editorMediaContainer,
        })}
        {channelFields}
      </ExpandedChannelSection.ChannelContent>
    </ExpandedChannelSection>
  )
}
